/**
 * ScaliqVault — Integraties-vault voor ScalIQ (app.scaliq.ai).
 *
 * Secties: 1) API key · 2) Overzicht (credits, LinkedIn-accounts + standaard
 * zoek-account, campagnes met stats) · 3) LinkedIn search-probe (dry-run) ·
 * 4) Webhooks (URL kopiëren + optioneel secret + recente events).
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";
import {
    KeyRound, Eye, EyeOff, CheckCircle2, Loader2, RefreshCw, Search,
    Copy, Webhook, Users, Megaphone, Coins, ChevronDown, ChevronRight,
} from "lucide-react";

function Sub({ title, action, testid, children }) {
    return (
        <div className="rounded-xl border border-white/10 bg-[var(--jr-surface-2)]/50 p-4" data-testid={testid}>
            <div className="flex items-center justify-between gap-2 mb-3">
                <h4 className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-2)]">{title}</h4>
                {action}
            </div>
            {children}
        </div>
    );
}

const CAMPAIGN_STATUS = {
    CAMPAIGN_IN_PROGRESS: { label: "Actief", cls: "text-[#22c55e] border-[#22c55e]/40" },
    CAMPAIGN_PAUSED: { label: "Gepauzeerd", cls: "text-[var(--jr-amber)] border-[var(--jr-amber)]/40" },
    READY_TO_SEND: { label: "Klaar", cls: "text-[var(--jr-info)] border-[var(--jr-info)]/40" },
};

export default function ScaliqVault({ cfg, onSaved, appBaseUrl }) {
    const { t } = useTranslation();
    const [apiKey, setApiKey] = useState("");
    const [reveal, setReveal] = useState(false);
    const [busy, setBusy] = useState(false);
    const [overview, setOverview] = useState(null);
    const [loadingOv, setLoadingOv] = useState(false);
    const [probeQ, setProbeQ] = useState("HR Director Belgium");
    const [probeBusy, setProbeBusy] = useState(false);
    const [probeResults, setProbeResults] = useState(null);
    const [secret, setSecret] = useState("");
    const [showAllCampaigns, setShowAllCampaigns] = useState(false);

    const webhookUrl = `${appBaseUrl || window.location.origin}/api/webhooks/scaliq`;

    const save = async () => {
        if (!apiKey.trim() && !cfg.configured) { toast.error(t("integr.enter_key")); return; }
        setBusy(true);
        try {
            await api.patch("/integrations/scaliq", { api_key: apiKey });
            toast.success(t("integr.key_saved", { name: "ScalIQ" }));
            setApiKey(""); setReveal(false); onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.save_failed")); }
        finally { setBusy(false); }
    };
    const remove = async () => {
        if (!window.confirm(t("integr.delete_key_confirm", { name: "ScalIQ" }))) return;
        setBusy(true);
        try { await api.delete("/integrations/scaliq"); toast.success(t("integr.deleted")); setOverview(null); onSaved(); }
        catch (e) { toast.error(e?.response?.data?.detail || t("integr.delete_failed")); }
        finally { setBusy(false); }
    };

    const loadOverview = async () => {
        setLoadingOv(true);
        try {
            const r = await api.get("/scaliq/overview");
            setOverview(r.data);
        } catch (e) { toast.error(e?.response?.data?.detail || t("scaliq.overview_failed")); }
        finally { setLoadingOv(false); }
    };

    // Auto-load bij openen van de kaart wanneer de key al gekoppeld is —
    // credits/quota's meteen zichtbaar zonder extra klik (reads = 0 credits).
    useEffect(() => {
        if (cfg.configured && !overview) loadOverview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cfg.configured]);

    const setDefaultAccount = async (accountId) => {
        try {
            await api.patch("/integrations/scaliq", { settings: { default_account_id: accountId } });
            setOverview((o) => o ? { ...o, default_account_id: accountId } : o);
            toast.success(t("scaliq.default_account_saved"));
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.save_failed")); }
    };

    const saveSecret = async () => {
        try {
            await api.patch("/integrations/scaliq", { settings: { webhook_secret: secret.trim() } });
            setSecret("");
            setOverview((o) => o ? { ...o, webhook_secret_set: !!secret.trim() } : o);
            toast.success(t("scaliq.secret_saved"));
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.save_failed")); }
    };

    const runProbe = async () => {
        if (probeQ.trim().length < 3) return;
        setProbeBusy(true);
        try {
            const r = await api.post("/scaliq/probe-search", { query: probeQ.trim(), limit: 5 });
            setProbeResults(r.data.results || []);
            toast.success(t("scaliq.probe_ok", { n: (r.data.results || []).length }));
        } catch (e) { setProbeResults(null); toast.error(e?.response?.data?.detail || t("integr.probe_failed")); }
        finally { setProbeBusy(false); }
    };

    const campaigns = overview?.campaigns || [];
    const visibleCampaigns = showAllCampaigns ? campaigns : campaigns.slice(0, 6);

    return (
        <div className="space-y-4">
            {/* 1 — API key */}
            <Sub title={`1. ${t("integr.sec_api_key")}`} testid="scaliq-credentials">
                <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">ScalIQ API Key</label>
                <div className="mt-1.5 flex gap-2">
                    <div className="flex-1 relative">
                        <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--jr-text-3)]" />
                        <input value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                            placeholder={cfg.api_key_masked || "sk_live_..."}
                            type={reveal ? "text" : "password"}
                            data-testid="scaliq-apikey-input"
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg pl-9 pr-10 py-2.5 text-sm text-white outline-none focus:border-[#6366f1]" />
                        <button type="button" onClick={() => setReveal((r) => !r)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[var(--jr-text-3)] hover:text-white">
                            {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                    </div>
                    <button onClick={save} disabled={busy} data-testid="scaliq-save"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#6366f1] text-white text-sm font-medium hover:bg-[#6366f1]/85 disabled:opacity-50">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} {t("common.save")}
                    </button>
                </div>
                <p className="mt-2 text-[10px] text-[var(--jr-text-3)]">
                    {t("scaliq.key_where")} <a href="https://app.scaliq.ai/api-mcp" target="_blank" rel="noreferrer" className="text-[var(--jr-text-2)] underline">app.scaliq.ai/api-mcp</a>.
                </p>
                {cfg.configured && (
                    <button onClick={remove} className="mt-2 text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] hover:text-[#ef4444]">
                        {t("integr.delete_current_key")}
                    </button>
                )}
            </Sub>

            {/* 2 — Overzicht */}
            <Sub title={`2. ${t("scaliq.sec_overview")}`} testid="scaliq-overview-section"
                action={
                    <button onClick={loadOverview} disabled={loadingOv || !cfg.configured} data-testid="scaliq-load-overview"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-[0.16em] bg-[#6366f1]/15 text-[#818cf8] border border-[#6366f1]/30 hover:bg-[#6366f1]/25 disabled:opacity-50">
                        {loadingOv ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />} {t("scaliq.load_overview")}
                    </button>
                }>
                {!overview ? (
                    <p className="text-xs text-[var(--jr-text-3)] italic">{t("scaliq.overview_hint")}</p>
                ) : (
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 flex-wrap" data-testid="scaliq-credits">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#6366f1]/40 text-[#818cf8] text-xs font-medium">
                                <Coins size={12} /> {overview.credits?.credits ?? 0} credits
                            </span>
                            <span className="text-[10px] font-mono uppercase text-[var(--jr-text-3)]">
                                {overview.credits?.planId || ""}
                            </span>
                        </div>

                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] mb-2 flex items-center gap-1.5">
                                <Users size={11} /> {t("scaliq.accounts_label")}
                            </div>
                            <div className="grid sm:grid-cols-2 gap-2">
                                {(overview.accounts || []).map((a, idx) => {
                                    const isDefault = overview.default_account_id === a.id
                                        || (!overview.default_account_id && idx === 0);
                                    const today = a.today || {};
                                    return (
                                        <button key={a.id} onClick={() => setDefaultAccount(a.id)}
                                            data-testid={`scaliq-account-${a.id}`}
                                            className={`text-left rounded-lg border px-3 py-2.5 ${isDefault
                                                ? "border-[#6366f1]/60 bg-[#6366f1]/10" : "border-white/10 hover:border-white/25"}`}>
                                            <div className="flex items-center gap-2">
                                                <span className={`w-1.5 h-1.5 rounded-full ${a.status === "connected" ? "bg-[#22c55e]" : "bg-[#ef4444]"}`} />
                                                <span className="text-sm text-white font-medium">{a.name}</span>
                                                {isDefault && <span className="ml-auto text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-full bg-[#6366f1]/20 text-[#818cf8]">{t("scaliq.default_badge")}</span>}
                                            </div>
                                            <div className="mt-1 text-[10px] text-[var(--jr-text-3)] font-mono">
                                                {t("scaliq.quota_line", {
                                                    c: today.connectionRequestsSent ?? 0, cl: today.connectionRequestLimit ?? "-",
                                                    m: today.messagesSent ?? 0, ml: today.messageLimit ?? "-",
                                                })}
                                                {a.hasSalesNavigator ? " · SalesNav" : ""}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="mt-1.5 text-[10px] text-[var(--jr-text-3)]">{t("scaliq.default_account_hint")}</p>
                        </div>

                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] mb-2 flex items-center gap-1.5">
                                <Megaphone size={11} /> {t("scaliq.campaigns_label", { n: campaigns.length })}
                            </div>
                            <div className="space-y-1.5" data-testid="scaliq-campaigns-list">
                                {visibleCampaigns.map((c) => {
                                    const st = CAMPAIGN_STATUS[c.status] || { label: c.status, cls: "text-[var(--jr-text-3)] border-white/15" };
                                    const stats = c.stats || {};
                                    return (
                                        <div key={c.id} className="flex items-center gap-3 rounded-lg border border-white/10 px-3 py-2">
                                            <span className={`shrink-0 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                                            <span className="text-xs text-white truncate flex-1" title={c.name}>{c.name}</span>
                                            <span className="shrink-0 text-[10px] font-mono text-[var(--jr-text-3)]">
                                                {c.totalLeads} leads · {stats.acceptanceRate ?? "-"}% acc · {stats.replyRate ?? "-"}% reply
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                            {campaigns.length > 6 && (
                                <button onClick={() => setShowAllCampaigns((s) => !s)}
                                    className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-mono uppercase text-[var(--jr-text-3)] hover:text-white">
                                    {showAllCampaigns ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                                    {showAllCampaigns ? t("scaliq.show_less") : t("scaliq.show_all", { n: campaigns.length })}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </Sub>

            {/* 3 — LinkedIn search-probe */}
            <Sub title={`3. ${t("scaliq.sec_probe")}`} testid="scaliq-probe-section">
                <div className="flex gap-2">
                    <input value={probeQ} onChange={(e) => setProbeQ(e.target.value)}
                        data-testid="scaliq-probe-query"
                        className="flex-1 bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#6366f1]" />
                    <button onClick={runProbe} disabled={probeBusy || !cfg.configured} data-testid="scaliq-probe-run"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#6366f1]/50 text-[#818cf8] text-sm font-medium hover:bg-[#6366f1]/10 disabled:opacity-50">
                        {probeBusy ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} {t("scaliq.probe_btn")}
                    </button>
                </div>
                <p className="mt-1.5 text-[10px] text-[var(--jr-text-3)]">{t("scaliq.probe_hint")}</p>
                {probeResults && (
                    <div className="mt-3 space-y-1.5" data-testid="scaliq-probe-results">
                        {probeResults.length === 0 && <p className="text-xs text-[var(--jr-text-3)]">{t("scaliq.probe_empty")}</p>}
                        {probeResults.map((r, i) => (
                            <a key={i} href={r.linkedinUrl} target="_blank" rel="noreferrer"
                                className="flex items-center gap-3 rounded-lg border border-white/10 px-3 py-2 hover:border-[#6366f1]/50">
                                {r.profilePictureUrl
                                    ? <img src={r.profilePictureUrl} alt="" className="w-7 h-7 rounded-full object-cover" />
                                    : <span className="w-7 h-7 rounded-full bg-[#6366f1]/20 grid place-items-center text-[10px] text-[#818cf8]">{(r.fullName || "?")[0]}</span>}
                                <div className="min-w-0">
                                    <div className="text-xs text-white truncate">{r.fullName}</div>
                                    <div className="text-[10px] text-[var(--jr-text-3)] truncate">{r.headline || r.jobTitle || ""}</div>
                                </div>
                                <span className="ml-auto shrink-0 text-[10px] text-[var(--jr-text-3)]">{r.location || ""}</span>
                            </a>
                        ))}
                    </div>
                )}
            </Sub>

            {/* 4 — Webhooks */}
            <Sub title={`4. ${t("scaliq.sec_webhooks")}`} testid="scaliq-webhook-section">
                <p className="text-xs text-[var(--jr-text-2)] mb-2">{t("scaliq.webhook_explain")}</p>
                <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-[var(--jr-surface-2)] px-3 py-2">
                    <Webhook size={13} className="text-[#818cf8] shrink-0" />
                    <code className="text-[11px] text-[var(--jr-text-2)] truncate flex-1" data-testid="scaliq-webhook-url">{webhookUrl}</code>
                    <button onClick={() => copyToClipboard(webhookUrl, t("common.copied"))}
                        data-testid="scaliq-webhook-copy"
                        className="p-1 rounded text-[var(--jr-text-3)] hover:text-white"><Copy size={13} /></button>
                </div>
                <div className="mt-3 flex gap-2">
                    <input value={secret} onChange={(e) => setSecret(e.target.value)}
                        placeholder={overview?.webhook_secret_set || cfg.settings?.webhook_secret_masked
                            ? t("scaliq.secret_set_ph") : t("scaliq.secret_ph")}
                        type="password" data-testid="scaliq-secret-input"
                        className="flex-1 bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#6366f1]" />
                    <button onClick={saveSecret} disabled={!secret.trim()} data-testid="scaliq-secret-save"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-[var(--jr-text-2)] text-sm hover:text-white hover:border-[#6366f1]/50 disabled:opacity-50">
                        <CheckCircle2 size={13} /> {t("common.save")}
                    </button>
                </div>
                <p className="mt-1.5 text-[10px] text-[var(--jr-text-3)]">{t("scaliq.secret_hint")}</p>
                {overview?.recent_events?.length > 0 && (
                    <div className="mt-3">
                        <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] mb-1.5">{t("scaliq.recent_events")}</div>
                        <div className="space-y-1" data-testid="scaliq-events-list">
                            {overview.recent_events.map((ev, i) => (
                                <div key={i} className="flex items-center gap-2 text-[11px] text-[var(--jr-text-2)]">
                                    <span className="font-mono text-[#818cf8]">{ev.type}</span>
                                    <span className="truncate">{ev.data?.fullName || ev.matched_id || ""}</span>
                                    <span className="ml-auto shrink-0 text-[10px] text-[var(--jr-text-3)]">{(ev.received_at || "").slice(0, 16).replace("T", " ")}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Sub>
        </div>
    );
}
